import api from "@/utils/api";
import { ApiResponse } from "@/constants/api";

// 리뷰 요약 데이터 인터페이스
export interface ReviewSummaryData {
    date: string;
    updatedAt: string;
    productAltKey: string;
    reviewCount: number;
    avgRating: number;
    avgSourness: number;
    avgSweetness: number;
    avgAromaStrength: number;
}

// 풍미 데이터 인터페이스
export interface FlavorData {
    label: string;
    value: number;
}

// 향 요약 데이터 인터페이스
export interface AromaSummaryData {
    date: string;
    productAltKey: string;
    aromaList: {
        aromaName: string;
        aromaCount: number;
    }[];
}

// 상품 평점 정보 인터페이스
export interface ProductRatingInfo {
    reviewCount: number;
    avgRating: number;
}

/**
 * 상품의 리뷰 요약 정보를 가져옵니다.
 * @param altKey 상품의 고유 키
 * @returns 리뷰 요약 데이터
 */
export const fetchProductReviewSummary = async (altKey: string): Promise<ReviewSummaryData | null> => {
    try {
        const response = await api.get<ApiResponse<ReviewSummaryData>>(
            `/api/v1/review-summary/product?productAltKey=${altKey}`
        );
        return response.data.data;
    } catch (error) {
        console.error("상품 리뷰 요약 정보 조회 실패:", error);
        return null;
    }
};

/**
 * 상품의 풍미 TOP5 정보를 가져옵니다.
 * @param altKey 상품의 고유 키
 * @returns 풍미 TOP5 데이터
 */
export const fetchProductFlavorTop5 = async (altKey: string): Promise<FlavorData[]> => {
    try {
        const response = await api.get<ApiResponse<AromaSummaryData>>(
            `/api/v1/review-summary/aroma?productAltKey=${altKey}`
        );

        if (response.data.success && response.data.data) {
            // API 응답 데이터를 FlavorData 형태로 변환
            const aromaSummary = response.data.data;

            // aromaList를 FlavorData 형식으로 변환하고, aromaCount를 기준으로 내림차순 정렬
            return aromaSummary.aromaList
                .map((aroma) => ({
                    label: aroma.aromaName,
                    value: aroma.aromaCount
                }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 5); // 상위 5개만 반환
        }

        return [];
    } catch (error) {
        console.error("상품 풍미 TOP5 정보 조회 실패:", error);
        return [];
    }
};

/**
 * 상품의 평점 정보(리뷰 수, 평균 평점)를 가져옵니다.
 * @param altKey 상품의 고유 키
 * @returns 평점 정보 데이터
 */
export const fetchProductRatingInfo = async (altKey: string): Promise<ProductRatingInfo | null> => {
    try {
        const reviewSummary = await fetchProductReviewSummary(altKey);
        if (reviewSummary) {
            return {
                reviewCount: reviewSummary.reviewCount,
                avgRating: reviewSummary.avgRating
            };
        }
        return null;
    } catch (error) {
        console.error("상품 평점 정보 조회 실패:", error);
        return null;
    }
};
